import React from 'react';
import TextField from '@mui/material/TextField';
import { Autocomplete as MUIAutocomplete } from '@mui/material';
import { AutocompleteProps as MUIAutocompleteProps } from '@mui/material';
import { AutocompleteValue as Value } from '@mui/material/useAutocomplete';
import {
  Control,
  Controller,
  ControllerRenderProps,
  RegisterOptions,
} from 'react-hook-form';
import { sanitizeAutoComplete } from './sanitize-auto-complete';

type ControllerProps = Omit<
  ControllerRenderProps<Record<string, any>>,
  'onChange'
>;
type AutocompleteProps<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> = Omit<
  MUIAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  'onChange' | 'defaultValue'
> & {
  rules?: Exclude<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs'
  >;
  control: Control<any>;
  name: string;
  label?: string;
  helperText?: string;
  required?: boolean;
  defaultValue?: T | T[];
  transformControllerProps?: (params: ControllerProps) => ControllerProps;
  onChange?: (
    data: Value<T, Multiple, DisableClearable, FreeSolo>,
  ) => Value<T, Multiple, DisableClearable, FreeSolo>;
};

export function Autocomplete<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>({
  onChange: externalOnChange,
  control,
  defaultValue,
  transformControllerProps,
  name,
  rules,
  label,
  renderInput, // explicitly omit renderInput
  ...rest
}: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>): React.ReactElement {
  return (
    <Controller
      render={({ field: { onChange, ...props } }) => {
        const transformedProps = transformControllerProps
          ? transformControllerProps(props)
          : props;
        return (
          <MUIAutocomplete
            onChange={(_, data) => {
              onChange(externalOnChange ? externalOnChange(data) : data);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                autoComplete={sanitizeAutoComplete(params.inputProps.autoComplete)}
              />
            )}
            {...transformedProps}
            {...rest}
          />
        );
      }}
      name={name}
      defaultValue={defaultValue}
      control={control}
      rules={rules}
    />
  );
}
